import * as React from "react";
import { FileText, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  PAYMENT_METHOD_LABELS,
  RECOVERY_PLAN_LABELS,
  formatDay,
  formatPaise,
  istToday,
  monthBounds,
  monthLabel,
  rupeesToPaise,
  shortMonthLabel,
  useLeaveBalance,
  useMemberAdvances,
  useMemberStatement,
  usePrepayAdvance,
  type Advance,
  type TeamPaymentMethod,
} from "@/lib/teamPay/api";
import { ErrorBanner, NativeSelect } from "@/components/team-pay/shared";
import { errorMessage } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — one member's running statement: what they earned, what
 * was added or taken off, what was paid and what is still owed. Signed
 * amounts read as the member's balance (positive = owed to them).
 */

function signed(paise: number): string {
  if (paise === 0) return formatPaise(0);
  return `${paise > 0 ? "+" : "−"}${formatPaise(Math.abs(paise))}`;
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "owed" | "muted" }) {
  return (
    <div className="border-border/60 rounded-lg border px-3 py-2.5">
      <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 text-base font-semibold tabular-nums",
          tone === "owed" && "text-primary",
          tone === "muted" && "text-muted-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function PrepayRow({ advance, canManage }: { advance: Advance; canManage: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState("");
  const [method, setMethod] = React.useState<TeamPaymentMethod>("cash");
  const [error, setError] = React.useState<string | null>(null);
  const prepay = usePrepayAdvance();
  const amountPaise = rupeesToPaise(amount);

  const submit = async () => {
    if (amountPaise === null) return;
    setError(null);
    try {
      await prepay.mutateAsync({
        id: advance.id,
        amount_paise: amountPaise,
        paid_on: istToday(),
        method,
      });
      toast.success(`${formatPaise(amountPaise)} paid back.`);
      setOpen(false);
      setAmount("");
    } catch (err) {
      setError(errorMessage(err, "Couldn't record the repayment."));
    }
  };

  return (
    <li className="border-border/60 space-y-2 rounded-lg border px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-sm font-medium tabular-nums">
            {formatPaise(advance.amount_paise)} on {formatDay(advance.given_on)}
          </p>
          <p className="text-muted-foreground text-xs">
            {RECOVERY_PLAN_LABELS[advance.plan]}
            {advance.plan === "emi" && advance.emi_months ? ` · ${advance.emi_months} months` : ""}
            {" · "}
            {formatPaise(advance.outstanding_paise)} left
          </p>
        </div>
        {canManage && advance.outstanding_paise > 0 && !open ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            Paid back
          </Button>
        ) : null}
      </div>
      {advance.schedule.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          {advance.schedule
            .map((s) => `${shortMonthLabel(s.year, s.month)} ${formatPaise(s.amount_paise)}`)
            .join(" · ")}
        </p>
      ) : null}
      {open ? (
        <div className="space-y-2">
          {error ? <ErrorBanner message={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Amount paid back (₹)"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              className="h-8 w-32"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={String(advance.outstanding_paise / 100)}
            />
            <NativeSelect
              aria-label="Paid back by"
              className="h-8 w-28"
              value={method}
              onChange={(e) => setMethod(e.target.value as TeamPaymentMethod)}
            >
              {(Object.keys(PAYMENT_METHOD_LABELS) as TeamPaymentMethod[]).map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </NativeSelect>
            <Button size="sm" disabled={amountPaise === null || prepay.isPending} onClick={submit}>
              {prepay.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

export function StatementSheet({
  open,
  onOpenChange,
  payeeId,
  memberName,
  year,
  month,
  canManage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payeeId: string | null;
  memberName: string;
  year: number;
  month: number;
  canManage: boolean;
}) {
  const bounds = monthBounds(year, month);
  const [from, setFrom] = React.useState(bounds.from);
  const [to, setTo] = React.useState(bounds.to);

  React.useEffect(() => {
    if (open) {
      const b = monthBounds(year, month);
      setFrom(b.from);
      setTo(b.to);
    }
  }, [open, year, month]);

  const activeId = open ? payeeId : null;
  const statement = useMemberStatement(activeId, from, to);
  const advances = useMemberAdvances(activeId);
  const balance = useLeaveBalance(activeId);
  const s = statement.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[600px]"
        data-testid="team-statement-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <FileText className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{memberName}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                Statement · {monthLabel(year, month)} by default. Change the dates to see any
                period.
              </SheetDescription>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              aria-label="From"
              type="date"
              className="h-8 w-40"
              value={from}
              max={to}
              onChange={(e) => e.target.value && setFrom(e.target.value)}
            />
            <span className="text-muted-foreground text-xs">to</span>
            <Input
              aria-label="To"
              type="date"
              className="h-8 w-40"
              value={to}
              min={from}
              onChange={(e) => e.target.value && setTo(e.target.value)}
            />
          </div>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {statement.isError ? (
            <ErrorBanner
              message={errorMessage(statement.error, "Couldn't load the statement.")}
              onRetry={() => statement.refetch()}
            />
          ) : null}

          {statement.isLoading || !s ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  className="bg-muted/60 h-16 rounded-lg motion-safe:animate-pulse"
                />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Metric label="Earned" value={formatPaise(s.earned_paise)} />
                <Metric label="Added" value={formatPaise(s.additions_paise)} />
                <Metric
                  label="Taken off"
                  value={formatPaise(s.deductions_paise + s.recoveries_paise)}
                />
                <Metric label="Paid" value={formatPaise(s.paid_paise)} />
                <Metric
                  label={s.closing_balance_paise >= 0 ? "Still owed" : "Paid ahead"}
                  value={formatPaise(Math.abs(s.closing_balance_paise))}
                  tone="owed"
                />
                <Metric
                  label="Advance left"
                  value={formatPaise(s.advance_outstanding_paise)}
                  tone={s.advance_outstanding_paise > 0 ? undefined : "muted"}
                />
              </div>

              {s.months.length > 0 ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-medium">How pay was worked out</h3>
                  <ul className="space-y-1.5">
                    {s.months.map((m) => (
                      <li
                        key={`${m.year}-${m.month}`}
                        className="bg-muted/30 rounded-lg px-3 py-2 text-xs leading-relaxed"
                      >
                        <span className="font-medium">{shortMonthLabel(m.year, m.month)}:</span>{" "}
                        {m.explanation}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section className="space-y-2">
                <h3 className="text-sm font-medium">Entries</h3>
                {s.opening_balance_paise !== 0 ? (
                  <p className="text-muted-foreground text-xs">
                    Opening balance {signed(s.opening_balance_paise)}
                  </p>
                ) : null}
                {s.lines.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Nothing in this period.</p>
                ) : (
                  <ul className="divide-border/60 divide-y" data-testid="statement-lines">
                    {s.lines.map((line, idx) => (
                      <li
                        key={`${line.day}-${line.kind}-${line.reference_id ?? idx}`}
                        className="flex items-baseline justify-between gap-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm">{line.label}</p>
                          <p className="text-muted-foreground text-xs" title={line.day}>
                            {formatDay(line.day)}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "shrink-0 text-sm tabular-nums",
                            line.amount_paise < 0 && "text-muted-foreground",
                          )}
                        >
                          {signed(line.amount_paise)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}

          {(advances.data?.items.length ?? 0) > 0 ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Advances</h3>
              <ul className="space-y-2">
                {advances.data?.items.map((a) => (
                  <PrepayRow key={a.id} advance={a} canManage={canManage} />
                ))}
              </ul>
            </section>
          ) : null}

          {balance.data ? (
            <section className="space-y-1">
              <h3 className="text-sm font-medium">Leave this year</h3>
              <p className="text-muted-foreground text-xs tabular-nums">
                {balance.data.available_days} paid days left · {balance.data.used_paid_days} used ·{" "}
                {balance.data.pending_days} waiting for approval
              </p>
            </section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
